<template>
  <v-container fluid>
    <v-layout column>
      <!-- Header -->
      <v-flex d-inline-flex align-center class="section-1 general-bg" py-2 px-3>
        <h2>Reportes SOS</h2>
        <v-spacer></v-spacer>
        <v-btn
          @click="fetchSOSData"
          :loading="loading"
          color="primary"
          small
        >
          <v-icon left>refresh</v-icon>
          Actualizar
        </v-btn>
      </v-flex>

      <!-- Loading State -->
      <v-flex v-if="loading && sosData.length === 0" class="text-xs-center" pt-5>
        <v-progress-circular
          :size="70"
          :width="7"
          color="primary"
          indeterminate
        ></v-progress-circular>
        <p class="mt-3">Cargando reportes SOS...</p>
      </v-flex>

      <!-- Error State -->
      <v-flex v-else-if="error" class="text-xs-center" pt-5>
        <v-icon size="100" color="error">error</v-icon>
        <h3 class="error--text mt-3">Error al cargar los reportes SOS</h3>
        <p class="mt-2">{{ error }}</p>
        <v-btn @click="fetchSOSData" color="primary" class="mt-3">
          <v-icon left>refresh</v-icon>
          Reintentar
        </v-btn>
      </v-flex>

      <!-- SOS Data Table -->
      <v-flex v-else-if="sosData.length > 0" ma-0 pt-3>
        <v-card>
          <v-card-title>
            <span class="headline">
              Reportes SOS ({{ totalRecords }} registros)
              <span v-if="!search && totalGroupedPages > 1" class="caption grey--text">
                - Página {{ currentPage }} de {{ totalGroupedPages }}
              </span>
            </span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Buscar por placa o código"
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="paginatedSOSData"
            :loading="loading"
            item-key="id"
            class="elevation-1"
            hide-actions
          >
            <!-- Custom row template -->
            <template v-slot:items="props">
              <tr @click="props.expanded = !props.expanded" class="sos-row">
                <!-- Quien (Código/Sigla + Placa) -->
                <td class="vehicle-info-cell">
                  <div class="vehicle-info">
                    <div class="vehicle-details">
                      <div class="vehicle-code">{{ props.item.vehicle.code }}</div>
                      <div class="vehicle-plate">{{ props.item.vehicle.license_plate }}</div>
                      <div v-if="props.item.count > 1" class="sos-count">{{ props.item.count }} SOS</div>
                    </div>
                  </div>
                </td>

                <!-- Que (Tipo de evento) -->
                <td class="event-type-cell">
                  <div class="event-type">Botón de pánico</div>
                </td>

                <!-- Agencia -->
                <td class="agency-cell">
                  <div class="agency-name">{{ getAgencyName(props.item.vehicle.agency_id) }}</div>
                </td>

                <!-- Último SOS -->
                <td class="last-sos-cell">
                  <div class="last-sos-time">{{ formatDate(props.item.latestSOS.event.time) }}</div>
                  <div v-if="props.item.count > 1" class="time-range">
                    {{ getTimeRange(props.item.sosItems) }}
                  </div>
                </td>

                <!-- Gestión (Botón para gestionar) -->
                <td class="management-action-cell text-xs-center">
                  <div class="management-status">
                    <v-icon
                      v-if="props.item.allManaged"
                      :color="getManagementIconColor(props.item)"
                      size="18"
                      :title="getManagementTooltip(props.item)"
                    >
                      {{ getManagementIcon(props.item) }}
                    </v-icon>
                    <v-btn
                      v-else-if="props.item.partiallyManaged"
                      @click.stop="handleGroupManagement(props.item)"
                      icon
                      small
                      :color="getManagementIconColor(props.item)"
                      class="management-btn"
                    >
                      <v-icon size="18">{{ getManagementIcon(props.item) }}</v-icon>
                    </v-btn>
                    <v-btn
                      v-else
                      @click.stop="handleGroupManagement(props.item)"
                      icon
                      small
                      color="grey"
                      class="management-btn"
                    >
                      <v-icon size="18">assignment</v-icon>
                    </v-btn>
                  </div>
                </td>

                <!-- Expand arrow -->
                <td class="expand-cell text-xs-center">
                  <v-icon size="20">{{ props.expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
                </td>
              </tr>
            </template>

            <!-- Expanded content -->
            <template v-slot:expand="props">
              <div class="sos-expanded-content pa-4">
                <!-- Información del Vehículo -->
                <v-card class="mb-4 elevation-2">
                  <v-card-title class="primary white--text clickable-vehicle-header" @click.stop="navigateToVehicle(props.item.vehicle)">
                    <v-icon left color="white">directions_car</v-icon>
                    Información del Vehículo
                    <v-spacer></v-spacer>
                    <v-icon color="white" size="18">open_in_new</v-icon>
                  </v-card-title>
                  <v-card-text class="pt-3">
                    <v-layout row wrap>
                      <v-flex xs12 sm6 md4>
                        <div class="info-item">
                          <span class="info-label">Placa:</span>
                          <span class="info-value clickable-vehicle-info" @click.stop="navigateToVehicle(props.item.vehicle)">{{ props.item.vehicle.license_plate }}</span>
                        </div>
                      </v-flex>
                      <v-flex xs12 sm6 md4>
                        <div class="info-item">
                          <span class="info-label">Código:</span>
                          <span class="info-value clickable-vehicle-info" @click.stop="navigateToVehicle(props.item.vehicle)">{{ props.item.vehicle.code }}</span>
                        </div>
                      </v-flex>
                      <v-flex xs12 sm6 md4>
                        <div class="info-item">
                          <span class="info-label">Agencia:</span>
                          <span class="info-value">{{ getAgencyName(props.item.vehicle.agency_id) }}</span>
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>

                <!-- Lista de SOS Agrupados -->
                <v-card class="mb-4 elevation-2">
                  <v-card-title class="error white--text">
                    <v-icon left color="white">warning</v-icon>
                    SOS Registrados ({{ props.item.count }})
                  </v-card-title>
                  <v-card-text class="pt-3">
                    <v-expansion-panel>
                      <v-expansion-panel-content
                        v-for="(sosItem, index) in props.item.sosItems"
                        :key="sosItem.id"
                      >
                        <div slot="header" class="sos-item-header">
                          <div class="sos-item-info">
                            <span class="sos-item-time">{{ formatDate(sosItem.event.time) }}</span>
                          </div>
                          <div class="sos-item-status">
                            <v-chip
                              v-if="sosItem.management && sosItem.management.length > 0"
                              :color="getSOSManagementChipColor(sosItem, props.item)"
                              text-color="white"
                              small
                            >
                              Gestionado
                            </v-chip>
                            <v-chip
                              v-else-if="sosItem.silence_actions && sosItem.silence_actions.length > 0"
                              color="warning"
                              text-color="white"
                              small
                            >
                              Silenciado
                            </v-chip>
                            <v-chip
                              v-else-if="sosItem.receipts && sosItem.receipts.length > 0"
                              color="info"
                              text-color="white"
                              small
                            >
                              Recibido
                            </v-chip>
                            <v-chip
                              v-else
                              color="error"
                              text-color="white"
                              small
                            >
                              Pendiente
                            </v-chip>
                          </div>
                        </div>

                        <v-card>
                          <v-card-text>
                            <v-layout row wrap>
                              <!-- Detalles del evento -->
                              <v-flex xs12 md6>
                                <h4 class="mb-2">Detalles del Evento</h4>
                                <div class="info-item">
                                  <span class="info-label">Dispositivo:</span>
                                  <span class="info-value">{{ sosItem.event.deviceId }}</span>
                                </div>
                                <div class="info-item">
                                  <span class="info-label">Velocidad:</span>
                                  <span class="info-value">{{ sosItem.event.speed }} km/h</span>
                                </div>
                                <div class="info-item">
                                  <span class="info-label">Ubicación:</span>
                                  <span class="info-value">{{ sosItem.event.latitude }}, {{ sosItem.event.longitude }}</span>
                                </div>
                                <div class="info-item">
                                  <span class="info-label">Rumbo:</span>
                                  <span class="info-value">{{ sosItem.event.direction }}°</span>
                                </div>
                              </v-flex>

                              <!-- Información de gestión -->
                              <v-flex xs12 md6>
                                <h4 class="mb-2">Estado y Gestión</h4>

                                <!-- Mostrar información de gestión si existe -->
                                <div v-if="sosItem.management && sosItem.management.length > 0">
                                  <div v-for="mgmt in sosItem.management" :key="mgmt.id" class="management-info mt-2">
                                    <v-card outlined class="pa-2">
                                      <div class="info-item">
                                        <span class="info-label">Tipo:</span>
                                        <span class="info-value">{{ translateAlertType(mgmt.alert_type) }}</span>
                                      </div>
                                      <div v-if="mgmt.notes" class="info-item">
                                        <span class="info-label">Notas:</span>
                                        <span class="info-value">{{ mgmt.notes }}</span>
                                      </div>
                                    </v-card>
                                  </div>
                                </div>

                                <!-- Botón para gestionar si no está gestionado -->
                                <div v-if="!sosItem.management || sosItem.management.length === 0" class="mt-2">
                                  <v-btn
                                    @click="openManagementDialog(sosItem)"
                                    color="primary"
                                    small
                                  >
                                    <v-icon left size="16">assignment</v-icon>
                                    Gestionar este SOS
                                  </v-btn>
                                </div>
                              </v-flex>
                            </v-layout>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-card-text>
                </v-card>

              </div>
            </template>

            <!-- No data template -->
            <template v-slot:no-data>
              <v-alert :value="true" color="info" icon="info">
                No se encontraron reportes SOS que coincidan con la búsqueda.
              </v-alert>
            </template>
          </v-data-table>

          <!-- Pagination -->
          <v-card-actions v-if="!search && totalGroupedPages > 1">
            <v-spacer></v-spacer>
            <v-pagination
              v-model="currentPage"
              :length="totalGroupedPages"
              :total-visible="7"
              @input="onPageChange"
            ></v-pagination>
            <v-spacer></v-spacer>
          </v-card-actions>

          <!-- Search results info -->
          <v-card-actions v-if="search">
            <v-spacer></v-spacer>
            <span class="caption">
              Mostrando {{ paginatedSOSData.length }} resultados de búsqueda
            </span>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-flex>

      <!-- Empty State -->
      <v-flex v-else class="text-xs-center" pt-5>
        <v-avatar size="200px" color="grey lighten-2">
          <v-icon size="120px" color="grey darken-1">warning</v-icon>
        </v-avatar>
        <h3 class="mt-3">No hay reportes SOS disponibles</h3>
        <p class="mt-2">No se encontraron reportes SOS en el sistema.</p>
        <v-btn @click="fetchSOSData" color="primary" class="mt-3">
          <v-icon left>refresh</v-icon>
          Actualizar
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- Diálogo de Gestión de SOS -->
    <v-dialog v-model="managementDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">assignment</v-icon>
          Gestionar SOS
        </v-card-title>

        <v-card-text class="pt-4" v-if="selectedSOS">
          <!-- Información del SOS -->
          <v-card class="mb-4" outlined>
            <v-card-text class="pb-2">
              <div class="subtitle-2 mb-2">Información del SOS</div>
              <div class="body-2">
                <strong>Vehículo:</strong> {{ selectedSOS.vehicle.code }} - {{ selectedSOS.vehicle.license_plate }}
              </div>
              <div class="body-2">
                <strong>Fecha:</strong> {{ formatDate(selectedSOS.event.time) }}
              </div>
              <div class="body-2">
                <strong>Agencia:</strong> {{ getAgencyName(selectedSOS.vehicle.agency_id) }}
              </div>
            </v-card-text>
          </v-card>

          <!-- Formulario de gestión -->
          <v-form ref="managementForm" v-model="formValid">
            <v-select
              v-model="managementForm.alert_type"
              :items="alertTypeOptions"
              label="Clasificación de la alerta *"
              required
              :rules="[v => !!v || 'La clasificación es requerida']"
              outlined
            ></v-select>

            <v-textarea
              v-model="managementForm.actions_taken"
              label="Acciones tomadas y observaciones *"
              placeholder="Describa las acciones realizadas..."
              required
              :rules="[v => !!v || 'Las acciones tomadas son requeridas']"
              outlined
              rows="4"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="closeManagementDialog"
            text
            :disabled="savingManagement"
          >
            Cancelar
          </v-btn>
          <v-btn
            @click="saveManagement"
            color="primary"
            :loading="savingManagement"
            :disabled="!formValid"
          >
            Guardar Gestión
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de Selección de SOS -->
    <v-dialog v-model="sosSelectionDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">list</v-icon>
          Seleccionar SOS para Gestionar
        </v-card-title>

        <v-card-text class="pt-4" v-if="selectedGroup">
          <div class="mb-3">
            <strong>Vehículo:</strong> {{ selectedGroup.vehicle.code }} - {{ selectedGroup.vehicle.license_plate }}
          </div>
          <div class="mb-4">
            <strong>{{ selectedGroup.count }} SOS encontrados en los últimos 5 minutos</strong>
          </div>

          <!-- Lista de SOS para seleccionar -->
          <v-list>
            <v-list-tile
              v-for="sosItem in getUnmanagedSOS(selectedGroup.sosItems)"
              :key="sosItem.id"
              @click="selectSOSForManagement(sosItem)"
              class="sos-selection-item"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  <div class="sos-selection-header">
                    <span class="sos-time">{{ formatDate(sosItem.event.time) }}</span>
                    <v-chip
                      v-if="sosItem.management && sosItem.management.length > 0"
                      color="success"
                      text-color="white"
                      small
                    >
                      Ya gestionado
                    </v-chip>
                    <v-chip
                      v-else-if="sosItem.silence_actions && sosItem.silence_actions.length > 0"
                      color="warning"
                      text-color="white"
                      small
                    >
                      Silenciado
                    </v-chip>
                    <v-chip
                      v-else
                      color="error"
                      text-color="white"
                      small
                    >
                      Pendiente
                    </v-chip>
                  </div>
                </v-list-tile-title>
                <v-list-tile-sub-title>
                  Velocidad: {{ sosItem.event.speed }} km/h |
                  Ubicación: {{ sosItem.event.latitude }}, {{ sosItem.event.longitude }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action v-if="!sosItem.management || sosItem.management.length === 0">
                <v-btn
                  color="primary"
                  small
                  @click.stop="selectSOSForManagement(sosItem)"
                >
                  Gestionar
                </v-btn>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="closeSosSelectionDialog"
          >
            Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  name: 'SOSReports',
  data() {
    return {
      sosData: [],
      loading: false,
      error: null,
      search: '',
      currentPage: 1,
      totalRecords: 0,
      totalPages: 0,
      itemsPerPage: 25, // Items to show per page after grouping
      serverLimit: 500, // Fetch more data from server to account for grouping
      // Gestión de SOS
      managementDialog: false,
      sosSelectionDialog: false,
      selectedGroup: null,
      selectedSOS: null,
      managementForm: {
        alert_type: '',
        actions_taken: ''
      },
      savingManagement: false,
      formValid: false,
      headers: [
        { text: 'Quién', value: 'vehicle_info', sortable: false, width: '200px' },
        { text: 'Qué', value: 'event_type', sortable: false, width: '150px' },
        { text: 'Agencia', value: 'agency', sortable: true, width: '250px' },
        { text: 'Último SOS', value: 'event.time', sortable: true, width: '180px' },
        { text: 'Gestión', value: 'management_action', sortable: false, width: '100px' },
        { text: '', value: 'expand', sortable: false, width: '50px' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      agenciesMap: 'agenciesMap'
    }),
    groupedSOSData() {
      // Agrupar SOS por vehículo y ventana de tiempo de 5 minutos
      const groups = []
      const processed = new Set()

      this.sosData.forEach(sos => {
        if (processed.has(sos.id)) return

        const sosTime = new Date(sos.event.time)
        const vehicleId = sos.vehicle.id

        // Buscar otros SOS del mismo vehículo en ventana de 5 minutos
        const relatedSOS = this.sosData.filter(otherSOS => {
          if (processed.has(otherSOS.id) || otherSOS.vehicle.id !== vehicleId) return false

          const otherTime = new Date(otherSOS.event.time)
          const timeDiff = Math.abs(sosTime - otherTime) / (1000 * 60) // diferencia en minutos

          return timeDiff <= 5
        })

        // Ordenar por fecha (más reciente primero)
        relatedSOS.sort((a, b) => new Date(b.event.time) - new Date(a.event.time))

        // Marcar como procesados
        relatedSOS.forEach(s => processed.add(s.id))

        // Crear grupo
        const isSOSManagedLocal = (sos) => {
          return (sos.management && sos.management.length > 0) ||
                 (sos.managed === true) ||
                 (sos.is_managed === true) ||
                 (sos.alert_management && sos.alert_management.length > 0)
        }

        const managedSOS = relatedSOS.filter(s => {
          const hasManagement = isSOSManagedLocal(s)
          
          console.log(`🔍 SOS ${s.id} - Gestión detectada:`, {
            management: s.management,
            managementLength: s.management ? s.management.length : 'N/A',
            managed: s.managed,
            is_managed: s.is_managed,
            alert_management: s.alert_management,
            hasManagement
          })
          
          return hasManagement
        })
        
        const unmanagedSOS = relatedSOS.filter(s => !isSOSManagedLocal(s))
        
        const hasRealEmergency = managedSOS.some(s => {
          // Verificar en diferentes estructuras posibles
          if (s.management && s.management.length > 0) {
            return s.management.some(m => m.alert_type === 'real_emergency')
          }
          if (s.alert_management && s.alert_management.length > 0) {
            return s.alert_management.some(m => m.alert_type === 'real_emergency')
          }
          // Si solo tiene flag de gestionado pero no detalles, asumir que no es emergencia real
          return false
        })

        const group = {
          id: `group_${vehicleId}_${sosTime.getTime()}`,
          vehicle: sos.vehicle,
          sosItems: relatedSOS,
          latestSOS: relatedSOS[0], // El más reciente
          count: relatedSOS.length,
          hasManaged: managedSOS.length > 0,
          allManaged: unmanagedSOS.length === 0,
          partiallyManaged: managedSOS.length > 0 && unmanagedSOS.length > 0,
          hasRealEmergency: hasRealEmergency,
          hasSilenced: relatedSOS.some(s => s.silence_actions && s.silence_actions.length > 0),
          totalReceipts: relatedSOS.reduce((sum, s) => sum + (s.receipts ? s.receipts.length : 0), 0)
        }

        // Debug logging para entender el estado de los grupos
        console.log(`🔍 Grupo SOS para vehículo ${sos.vehicle.code}:`, {
          vehicleCode: sos.vehicle.code,
          totalSOS: relatedSOS.length,
          managedCount: managedSOS.length,
          unmanagedCount: unmanagedSOS.length,
          hasManaged: group.hasManaged,
          allManaged: group.allManaged,
          partiallyManaged: group.partiallyManaged,
          hasRealEmergency: group.hasRealEmergency,
          managementData: managedSOS.map(s => ({
            id: s.id,
            management: s.management,
            alertTypes: s.management ? s.management.map(m => m.alert_type) : []
          }))
        })

        groups.push(group)
      })

      // Ordenar grupos por fecha más reciente
      return groups.sort((a, b) => new Date(b.latestSOS.event.time) - new Date(a.latestSOS.event.time))
    },

    filteredSOSData() {
      if (!this.search) {
        return this.groupedSOSData
      }
      const searchTerm = this.search.toLowerCase()
      return this.groupedSOSData.filter(group => {
        return (
          group.vehicle.license_plate.toLowerCase().includes(searchTerm) ||
          group.vehicle.code.toLowerCase().includes(searchTerm)
        )
      })
    },

    // Calculate total pages based on grouped data
    totalGroupedPages() {
      const totalGroups = this.search ? this.filteredSOSData.length : this.groupedSOSData.length
      return Math.ceil(totalGroups / this.itemsPerPage)
    },

    paginatedSOSData() {
      const dataToPage = this.search ? this.filteredSOSData : this.groupedSOSData
      const startIndex = (this.currentPage - 1) * this.itemsPerPage
      const endIndex = startIndex + this.itemsPerPage
      const result = dataToPage.slice(startIndex, endIndex)


      return result
    },
    alertTypeTranslations() {
      return {
        'real_emergency': 'Emergencia real',
        'accidental_activation': 'Activación accidental',
        'test_maintenance': 'Prueba / Mantenimiento',
        'could_not_confirm': 'No se pudo confirmar'
      }
    },
    alertTypeOptions() {
      return [
        { text: 'Emergencia real', value: 'real_emergency' },
        { text: 'Activación accidental', value: 'accidental_activation' },
        { text: 'Prueba / Mantenimiento', value: 'test_maintenance' },
        { text: 'No se pudo confirmar', value: 'could_not_confirm' }
      ]
    },
    sosEndpoint() {
      // Use different endpoints based on environment
      if (process.env.NODE_ENV === 'production') {
        return 'https://api.omnitrack.com.co/v1/sos'
      } else {
        return 'https://ruzr8u4gsd.execute-api.us-east-1.amazonaws.com/testing/v1/sos'
      }
    }
  },
  watch: {
    search(newVal, oldVal) {
      // Reset to first page when search changes
      if (newVal !== oldVal && this.currentPage !== 1) {
        this.currentPage = 1
      }
    }
  },
  async mounted() {
    // Asegurar que el store de agencias esté cargado
    try {
      if (!this.agenciesMap || this.agenciesMap.size === 0) {
        await this.$store.dispatch('fetchAgenciesTreeAction')
      }
    } catch (error) {
      console.warn('Error loading agencies:', error)
    }

    this.fetchSOSData()
  },
  methods: {
    // Función auxiliar para detectar si un SOS está gestionado
    isSOSManaged(sos) {
      return (sos.management && sos.management.length > 0) ||
             (sos.managed === true) ||
             (sos.is_managed === true) ||
             (sos.alert_management && sos.alert_management.length > 0)
    },

    // Método temporal para agregar datos de gestión de prueba
    addTestManagementData() {
      console.log('🧪 Agregando datos de gestión de prueba...')
      console.log('🧪 Total SOS disponibles:', this.sosData.length)
      
      if (this.sosData.length >= 1) {
        // Primer SOS: Gestionado como emergencia real (debería ser VERDE)
        console.log('🧪 Antes - SOS 1:', this.sosData[0].management)
        this.sosData[0].management = [{
          alert_type: 'real_emergency',
          notes: 'Emergencia confirmada',
          user_id: 'test_user'
        }]
        console.log('✅ SOS 1: Configurado como emergencia real', this.sosData[0].management)
      }
      
      if (this.sosData.length >= 2) {
        // Segundo SOS: Gestionado como falsa alarma (debería ser ROJO)
        console.log('🧪 Antes - SOS 2:', this.sosData[1].management)
        this.sosData[1].management = [{
          alert_type: 'accidental_activation',
          notes: 'Activación accidental',
          user_id: 'test_user'
        }]
        console.log('✅ SOS 2: Configurado como activación accidental', this.sosData[1].management)
      }
      
      if (this.sosData.length >= 4) {
        // Cuarto SOS: Parcialmente gestionado (debería ser NARANJA)
        console.log('🧪 Antes - SOS 4:', this.sosData[3].management)
        this.sosData[3].management = [{
          alert_type: 'could_not_confirm',
          notes: 'No se pudo confirmar',
          user_id: 'test_user'
        }]
        console.log('✅ SOS 4: Configurado para prueba de gestión parcial', this.sosData[3].management)
      }
      
      // El resto permanecen sin gestionar (GRIS)
      console.log('🧪 Datos de prueba agregados exitosamente')
      
      // Forzar actualización de la vista
      this.$forceUpdate()
    },

    async fetchSOSData() {
      this.loading = true
      this.error = null

      try {
        const params = {
          page: 1, // Always fetch from page 1 since we'll handle pagination after grouping
          limit: this.serverLimit
        }

        const response = await axios.get(this.sosEndpoint, { params })

        const data = response.data

        this.sosData = data.sos || []
        this.totalRecords = data.total_records || 0

        // Calculate total pages based on grouped data, not raw data
        // We'll update this after grouping is computed

        console.log('SOS Data loaded:', {
          rawSOSCount: this.sosData.length,
          totalRecords: this.totalRecords,
          serverLimit: this.serverLimit
        })

        // Debug: Mostrar estructura de los primeros SOS para entender el formato
        if (this.sosData.length > 0) {
          console.log('📋 Estructura de datos SOS (primeros 3):', this.sosData.slice(0, 3).map(sos => ({
            id: sos.id,
            vehicleCode: sos.vehicle && sos.vehicle.code,
            management: sos.management,
            managed: sos.managed,
            is_managed: sos.is_managed,
            alert_management: sos.alert_management,
            hasManagement: this.isSOSManaged(sos),
            managementTypes: sos.management ? sos.management.map(m => m.alert_type) : []
          })))

          // Para propósitos de prueba, agregar datos de gestión simulados a algunos SOS
          // Esto se puede remover una vez que se confirme que la lógica funciona
          // console.log('🧪 NODE_ENV:', process.env.NODE_ENV)
          // this.addTestManagementData() // Comentado: ahora se usan los datos reales del backend
        }

      } catch (error) {
        console.error('Error fetching SOS data:', error)
        this.error = (error.response && error.response.data && error.response.data.message) || error.message || 'Error desconocido'
        this.sosData = []
        this.totalRecords = 0
        this.totalPages = 0
      } finally {
        this.loading = false
      }
    },

    onPageChange(page) {
      this.currentPage = page
      // No need to fetch data again since we handle pagination in frontend
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'

      try {
        const date = new Date(dateString)
        return date.toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },

    translateAlertType(alertType) {
      return this.alertTypeTranslations[alertType] || alertType
    },

    getAgencyName(agencyId) {
      if (!agencyId) {
        return 'Sin agencia'
      }

      // Verificar si agenciesMap existe y es un Map
      if (!this.agenciesMap || typeof this.agenciesMap.get !== 'function') {
        return `Agencia ${agencyId}`
      }

      try {
        const agency = this.agenciesMap.get(agencyId)
        return agency && agency.name ? agency.name : `Agencia ${agencyId}`
      } catch (error) {
        console.warn('Error accessing agenciesMap:', error)
        return `Agencia ${agencyId}`
      }
    },


    openManagementDialog(sosItem) {
      this.selectedSOS = sosItem
      this.managementForm = {
        alert_type: '',
        actions_taken: ''
      }
      this.managementDialog = true
    },

    closeManagementDialog() {
      this.managementDialog = false
      this.selectedSOS = null
      this.managementForm = {
        alert_type: '',
        actions_taken: ''
      }
    },

    async saveManagement() {
      if (!this.formValid || !this.selectedSOS) return

      this.savingManagement = true

      try {
        const managementData = {
          sos_id: this.selectedSOS.id,
          user_id: this.getUserId(),
          alert_type: this.managementForm.alert_type,
          notes: this.managementForm.actions_taken,
          status: 'in_progress',
          priority: this.managementForm.alert_type === 'real_emergency' ? 'high' : 'medium',
          client_id: this.getClientId()
        }

        // Si es emergencia real, agregar flags adicionales
        if (this.managementForm.alert_type === 'real_emergency') {
          managementData.follow_up_required = true
        }

        console.log('📤 Enviando gestión de alerta:', managementData)

        const response = await fetch('https://c2q0025gnc.execute-api.us-east-1.amazonaws.com/prod/sos-management', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Environment': process.env.NODE_ENV === 'production' ? 'production' : 'testing'
          },
          body: JSON.stringify(managementData)
        })

        const result = await response.json()

        if (response.ok && result.success) {
          // Actualizar los datos locales
          await this.fetchSOSData()

          // Cerrar el diálogo
          this.closeManagementDialog()

          // Mostrar mensaje de éxito
          console.log('✅ Gestión de alerta guardada exitosamente:', result)
        } else {
          console.error('❌ Error al guardar la gestión:', result)
          throw new Error('Error en la respuesta del servidor')
        }

      } catch (error) {
        console.error('❌ Error al gestionar SOS:', error)
        // Aquí podrías mostrar un mensaje de error al usuario
      } finally {
        this.savingManagement = false
      }
    },

    getUserId() {
      const userProfile = this.$store.getters['auth/getProfile']
      return (userProfile && userProfile.sub) || (userProfile && userProfile.id) || 'anonymous'
    },

    getClientId() {
      let clientId = localStorage.getItem('pusher_client_id')
      if (!clientId) {
        clientId = 'client_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
        localStorage.setItem('pusher_client_id', clientId)
      }
      return clientId
    },

    getTimeRange(sosItems) {
      if (sosItems.length <= 1) return ''

      const times = sosItems.map(item => new Date(item.event.time))
      const earliest = new Date(Math.min(...times))
      const latest = new Date(Math.max(...times))

      const diffMinutes = Math.round((latest - earliest) / (1000 * 60))

      return `(${diffMinutes} min)`
    },

    handleGroupManagement(group) {
      // Si solo hay un SOS o solo uno sin gestionar, ir directo al formulario
      const unmanagedSOS = this.getUnmanagedSOS(group.sosItems)

      if (unmanagedSOS.length === 1) {
        this.openManagementDialog(unmanagedSOS[0])
      } else if (unmanagedSOS.length > 1) {
        // Mostrar selector de SOS
        this.selectedGroup = group
        this.sosSelectionDialog = true
      }
      // Si no hay SOS sin gestionar, no hacer nada (el botón no debería aparecer)
    },

    getUnmanagedSOS(sosItems) {
      return sosItems.filter(sos => !this.isSOSManaged(sos))
    },

    selectSOSForManagement(sosItem) {
      this.closeSosSelectionDialog()
      this.openManagementDialog(sosItem)
    },

    closeSosSelectionDialog() {
      this.sosSelectionDialog = false
      this.selectedGroup = null
    },

    getManagementIconColor(group) {
      console.log(`🎨 Determinando color para vehículo ${group.vehicle.code}:`, {
        hasManaged: group.hasManaged,
        partiallyManaged: group.partiallyManaged,
        allManaged: group.allManaged,
        hasRealEmergency: group.hasRealEmergency
      })

      if (!group.hasManaged) {
        // Gris si ninguna alerta ha sido gestionada
        console.log(`➡️ Color: GRIS (sin gestionar) para ${group.vehicle.code}`)
        return 'grey'
      } else if (group.partiallyManaged) {
        // Naranja si hay alertas gestionadas pero falta alguna por gestionar
        console.log(`➡️ Color: NARANJA (parcialmente gestionado) para ${group.vehicle.code}`)
        return 'orange'
      } else if (group.allManaged) {
        if (group.hasRealEmergency) {
          // Verde si todas están gestionadas y al menos una es emergencia real
          console.log(`➡️ Color: VERDE (todas gestionadas + emergencia real) para ${group.vehicle.code}`)
          return 'success'
        } else {
          // Rojo si todas están gestionadas pero ninguna es emergencia real
          console.log(`➡️ Color: ROJO (todas gestionadas + sin emergencia real) para ${group.vehicle.code}`)
          return 'error'
        }
      }
      console.log(`➡️ Color: GRIS (default) para ${group.vehicle.code}`)
      return 'grey'
    },

    getManagementIcon(group) {
      if (!group.hasManaged) {
        return 'assignment'
      } else if (group.partiallyManaged) {
        return 'assignment_late'
      } else {
        return 'check_circle'
      }
    },

    getManagementTooltip(group) {
      if (group.allManaged && group.hasRealEmergency) {
        return 'Todas las alertas gestionadas - Incluye emergencia real'
      } else if (group.allManaged && !group.hasRealEmergency) {
        return 'Todas las alertas gestionadas - Sin emergencias reales'
      } else if (group.partiallyManaged) {
        return 'Algunas alertas gestionadas - Faltan por gestionar'
      } else {
        return 'Ninguna alerta gestionada'
      }
    },

    navigateToVehicle(vehicle) {
      // Navegar a la vista del vehículo usando Vue Router
      console.log('🚗 Navegando a vehículo:', vehicle)
      console.log('🚗 Vehicle ID:', vehicle.id)
      console.log('🚗 Router disponible:', !!this.$router)

      if (!vehicle.id) {
        console.error('❌ Vehicle ID no disponible:', vehicle)
        return
      }

      const targetRoute = `/vehicles/${vehicle.id}`
      console.log('🚗 Ruta objetivo:', targetRoute)

      try {
        if (this.$router) {
          console.log('🚗 Usando Vue Router...')
          this.$router.push(targetRoute)
        } else {
          console.log('🚗 Router no disponible, usando window.location...')
          window.location.href = `#${targetRoute}`
        }
      } catch (error) {
        console.error('❌ Error al navegar al vehículo:', error)
        // Fallback: intentar con window.location
        console.log('🚗 Usando fallback con window.location...')
        window.location.href = `#${targetRoute}`
      }
    },

    getSOSManagementChipColor(sosItem, group) {
      // Si el SOS no está gestionado, no aplicar color
      if (!sosItem.management || sosItem.management.length === 0) {
        return 'success'
      }

      // Verificar si este SOS específico tiene emergencia real
      const hasRealEmergency = sosItem.management.some(m => m.alert_type === 'real_emergency')

      if (hasRealEmergency) {
        // Verde si este SOS específico es emergencia real
        return 'success'
      } else {
        // Rojo si este SOS está gestionado pero no es emergencia real
        return 'error'
      }
    }
  }
}
</script>

<style scoped>
.sos-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.sos-row:hover {
  background-color: #f5f5f5;
}

/* Vista expandida */
.sos-expanded-content {
  background-color: transparent;
  border-radius: 8px;
  margin: 0 16px;
}

/* Elementos de información */
.info-item {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
}

.info-label {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
}

/* Tarjetas de usuario */
.user-card {
  border-radius: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
  border-left: 4px solid #e0e0e0;
  background-color: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
}

.user-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.user-info {
  width: 100%;
}

.user-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.user-id {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  word-break: break-all;
}

.user-actions {
  margin: 8px 0;
}

.user-details {
  margin-top: 8px;
}

.detail-item {
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin-bottom: 2px;
}

.detail-value {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  word-break: break-word;
}

/* Colores específicos para las tarjetas de usuario */
.user-card.received {
  border-left-color: #2196f3;
}

.user-card.silenced {
  border-left-color: #ff9800;
}

.user-card.managed {
  border-left-color: #4caf50;
}

/* Chips más pequeños y elegantes */
.v-chip--small {
  height: 22px;
  font-size: 11px;
  font-weight: 500;
}

/* Títulos de las tarjetas */
.v-card__title {
  font-size: 16px;
  font-weight: 600;
  padding: 12px 16px;
}

/* Espaciado mejorado */
.v-card__text {
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Estilos específicos para las tarjetas principales */
.sos-expanded-content .v-card {
  background-color: rgba(255, 255, 255, 0.08) !important;
  margin-bottom: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Títulos con fondo de color y texto blanco */
.sos-expanded-content .v-card .v-card__title {
  color: white !important;
}

/* Contenido de información en las tarjetas principales */
.sos-expanded-content .v-card .v-card__text .info-item .info-label {
  color: rgba(255, 255, 255, 0.7) !important;
}

.sos-expanded-content .v-card .v-card__text .info-item .info-value {
  color: rgba(255, 255, 255, 0.95) !important;
}

/* Mensaje cuando no hay acciones */
.sos-expanded-content .v-card .v-card__text .grey--text {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Chips en el contenido */
.sos-expanded-content .v-chip {
  color: white !important;
}

/* Iconos en la vista expandida */
.sos-expanded-content .v-icon {
  color: rgba(255, 255, 255, 0.8) !important;
}

/* Responsive */
@media (max-width: 768px) {
  .sos-expanded-content {
    margin: 0 8px;
    padding: 16px;
  }

  .info-item {
    margin-bottom: 16px;
  }

  .user-card {
    margin-bottom: 16px;
  }

  .user-card-compact {
    margin-bottom: 8px;
    width: 100% !important;
    max-width: 100% !important;
  }
}

/* Estilos para cards compactos */
.user-card-compact {
  transition: all 0.2s ease;
  border-radius: 8px;
  min-height: 80px;
  width: 280px;
  max-width: 280px;
}

.user-card-compact:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.user-card-compact .v-card__text {
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.user-card-compact .text-truncate {
  max-width: 180px;
}

/* Mejorar espaciado de títulos */
.v-card__title.py-2 {
  min-height: 48px;
}

/* Estilos para las celdas de la tabla */
.vehicle-info-cell {
  padding: 12px 16px !important;
  vertical-align: top !important;
}

.vehicle-info-cell .vehicle-info {
  width: 100%;
  text-align: left;
}

.vehicle-info {
  display: flex;
  align-items: flex-start;
  gap: 0px;
  padding: 0;
  margin: 0;
}

.vehicle-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.vehicle-code {
  font-weight: 600;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.2;
}

.vehicle-plate {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.2;
}

.event-type-cell {
  padding: 12px 16px !important;
}

.event-type {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
}

.agency-cell {
  padding: 12px 16px !important;
}

.agency-name {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
}

.last-sos-cell {
  padding: 12px 16px !important;
}

.last-sos-time {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
}

.management-action-cell {
  padding: 12px 16px !important;
}

.management-btn {
  transition: all 0.2s ease;
}

.management-btn:hover {
  transform: scale(1.1);
}

.expand-cell {
  padding: 12px 16px !important;
}

/* Hover effect para las filas */
.sos-row:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Estilos para la tabla */
.v-data-table tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.v-data-table thead th {
  color: rgba(255, 255, 255, 0.8) !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2) !important;
}

/* Estilos para agrupación de SOS */
.sos-count {
  font-size: 0.75em;
  color: #ff9800;
  font-weight: 500;
  margin-top: 2px;
}

.time-range {
  font-size: 0.75em;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

.management-status {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Estilos para items de SOS individuales */
.sos-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.sos-item-info {
  display: flex;
  flex-direction: column;
}

.sos-item-time {
  font-weight: 500;
  color: #ffffff;
}

.sos-item-id {
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.7);
}

.sos-item-status {
  display: flex;
  align-items: center;
}

.management-info {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  padding: 8px;
}

/* Expansion panel styling */
.v-expansion-panel__header {
  background: rgba(255, 255, 255, 0.05) !important;
  color: #ffffff !important;
}

.v-expansion-panel__body {
  background: rgba(255, 255, 255, 0.02) !important;
}

.v-expansion-panel {
  background: transparent !important;
}

.v-expansion-panel-content {
  background: transparent !important;
}

/* Estilos para el diálogo de selección de SOS */
.sos-selection-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background-color 0.2s;
}

.sos-selection-item:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.sos-selection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.sos-time {
  font-weight: 500;
  color: #ffffff;
}

.v-list__tile__sub-title {
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 0.85em;
}

.v-list__tile__title {
  color: rgba(255, 255, 255, 0.9) !important;
}

/* Estilos para navegación al vehículo en vista expandida */
.clickable-vehicle-header {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-vehicle-header:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.clickable-vehicle-info {
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px 4px;
  border-radius: 3px;
}

.clickable-vehicle-info:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
  text-decoration: underline;
}

</style>



// WEBPACK FOOTER //
// src/components/SOSReports.vue